import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  ICompany,
  InputSize,
  InputState,
  IUser,
  UserRole
} from '../../../../../assets/types/dtoTypes';
import { SettingsPageService } from './settings-page.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { Subject, filter, switchMap, takeUntil } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PATH } from '../../../../../assets/constants';
import { DeleteAccountModalComponent } from '../../../../modals/delete-account-modal/delete-account-modal.component';
import { ImageService } from '../../../../shared/services/image.service';
import { AzureService } from '../../../../shared/services/azure.service';
import { ThemeService } from '../../../../shared/services/theme.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { BIPPERZ_BREAKPOINTS, BreakpointsService } from '../../../../shared/services/breakpoint.service';
import { ExternalHeaderComponent } from '../../../../shared/external-header/external-header.component';
import { NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../../../../shared/input/input.component';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FooterComponent } from '../../../../shared/footer/footer.component';
import { LoadMaskComponent } from '../../../../shared/load-mask/load-mask.component';

@Component({
  selector: 'app-settings-page',
  templateUrl: './settings-page.component.html',
  styleUrls: ['./settings-page.component.scss'],
  imports: [
    ExternalHeaderComponent,
    TranslateModule,
    NgSwitch,
    NgSwitchCase,
    InputComponent,
    FormsModule,
    ButtonComponent,
    MatSlideToggle,
    FooterComponent,
    LoadMaskComponent,
    NgIf,
    NgClass,
    RouterLink
  ],
  standalone: true
})
export class SettingsPageComponent implements OnInit, OnDestroy {
  private readonly azureService = inject(AzureService);
  private readonly imageService = inject(ImageService);
  private readonly settingsPageService = inject(SettingsPageService);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly themeService = inject(ThemeService);
  private readonly showPopupService = inject(ShowPopupService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  protected readonly authService = inject(AuthenticationService);

  private readonly destroy$ = new Subject<void>();

  public isBackendError = false;
  public backendError = '';
  public isLoading = false;
  public isMobile = false;

  public id = '';
  public selectedSetting = 'name';

  public user: Partial<IUser> | null = null;
  public company: Partial<ICompany> | null = null;

  public userEmail = '';
  public currentPassword = '';
  public newPassword = '';
  public confirmNewPassword = '';

  public showCurrentPassword = false;
  public showNewPassword = false;
  public showConfirmPassword = false;

  public isCurrentPasswordError = false;
  public isConfirmPasswordError = false;

  protected readonly InputSize = InputSize;
  protected readonly InputState = InputState;
  protected readonly ButtonType = ButtonType;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonSize = ButtonSize;

  constructor() {
    this.observeBreakpoints();
  }

  ngOnInit(): void {
    this.userEmail = this.authService.getEmailFromAccessToken();
    this.loadProfile();
  }

  get displayName(): string {
    if (this.isCompanyProfile()) {
      return this.company?.name ?? '';
    }

    return this.user?.username ?? '';
  }

  get displayExpertise(): string {
    if (this.isCompanyProfile()) {
      return this.company?.industry ?? '';
    }

    return this.user?.expertise ?? '';
  }

  get displayImage(): string {
    if (this.isCompanyProfile()) {
      return this.company?.logoUrl ?? '';
    }

    return this.user?.profileImageUrl ?? '';
  }

  get hasProfileData(): boolean {
    return !!this.user || !!this.company;
  }

  selectSetting(setting: string): void {
    this.selectedSetting = setting;
  }

  submitPasswordChange(): void {
    this.isBackendError = false;
    this.isCurrentPasswordError = false;
    this.isConfirmPasswordError = false;

    if (!this.currentPassword) {
      this.isCurrentPasswordError = true;
      return;
    }

    if (this.newPassword !== this.confirmNewPassword) {
      this.isConfirmPasswordError = true;
      return;
    }

    this.isLoading = true;

    this.settingsPageService.changePassword(
      this.currentPassword,
      this.newPassword,
      this.confirmNewPassword
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showPopupService.showPopup({
            status: 'isSuccess',
            translationKey: 'job_details.popup.application.password.success',
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.backendError = error?.error?.message ?? '';
          this.isBackendError = true;
        },
      });
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    this.setImageFile(input.files[0]);
  }

  setImageFile(file: File): void {
    this.isLoading = true;

    if (this.isCompanyProfile()) {
      this.updateCompanyImage(file);
      return;
    }

    this.updateUserImage(file);
  }

  isCompanyProfile(): boolean {
    return this.authService.getUserRoleFromAccessToken() === UserRole.PARTNER;
  }

  isAgencyProfile(): boolean {
    return this.authService.getUserRoleFromAccessToken() === UserRole.AGENCY;
  }

  isProfile(): boolean {
    return this.authService.getUserRoleFromAccessToken() === UserRole.USER;
  }

  logout(): void {
    this.authService.logout();
  }

  /**
   * Opens the confirmation dialog and, only if the user explicitly confirms,
   * permanently deletes the account, clears the session and returns to login.
   */
  openDeleteAccountDialog(): void {
    this.dialog.open(DeleteAccountModalComponent, {
      width: '480px',
      autoFocus: false,
    })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed === true),
        switchMap(() => {
          this.isLoading = true;
          return this.authService.deleteAccount();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.showPopupService.showPopup({
            status: 'isSuccess',
            translationKey: 'settings.danger_zone.delete_success',
          });
          this.router.navigate([PATH.LOGIN]);
        },
        error: () => {
          this.isLoading = false;
          this.showPopupService.showPopup({
            status: 'failure',
            translationKey: 'settings.danger_zone.delete_error',
          });
        },
      });
  }

  togglePasswordVisibility(field: string): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadProfile(): void {
    this.isLoading = true;

    if (this.isCompanyProfile()) {
      this.loadCompanyProfile();
      return;
    }

    this.loadUserProfile();
  }

  private loadCompanyProfile(): void {
    this.id = this.authService.getCompanyIdFromAccessToken();

    this.settingsPageService.getCompany(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (company: ICompany) => {
          this.company = company;
          this.imageService.appendSasTokenToCompanyImage(company.logoUrl, company.id, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (updatedCompany) => {
                this.company.logoUrl = updatedCompany;
                this.isLoading = false;
              },
              error: (error) => {
                console.error(error);
                this.isLoading = false;
              }
            });
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        }
      });
  }

  private loadUserProfile(): void {
    this.id = this.authService.getIdFromAccessToken();

    this.settingsPageService.getUser(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user: IUser) => {
          this.imageService.appendSasTokenToProfileImage(user)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (updatedUser) => {
                this.user = updatedUser;
                this.isLoading = false;
              },
              error: (error) => {
                console.error(error);
                this.isLoading = false;
              }
            });
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        }
      });
  }

  private updateCompanyImage(file: File): void {
    this.azureService.updateCompanyImage(this.id, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedCompany) => {
          this.imageService.appendSasTokenToCompanyImage(updatedCompany.logoUrl, this.id, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (logoUrl) => {
                if (this.company) {
                  this.company = {
                    ...this.company,
                    logoUrl
                  };
                }

                this.isLoading = false;
              },
              error: (error) => {
                console.error(error);
                this.isLoading = false;
              }
            });
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        }
      });
  }

  private updateUserImage(file: File): void {
    this.azureService.updateProfileImage(this.id, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedUser) => {
          const mergedUser: IUser = {
            ...(this.user as IUser),
            ...updatedUser
          };

          this.imageService.appendSasTokenToProfileImage(mergedUser)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (userWithSas) => {
                this.user = userWithSas;
                this.isLoading = false;
              },
              error: (error) => {
                console.error(error);
                this.isLoading = false;
              }
            });
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        }
      });
  }

  private observeBreakpoints(): void {
    this.breakpointsService.observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.s || result.m_down;
      });
  }
}
